// src/hooks/signalProblemesModifies.ts — SCE Issues
// Recharger les listes de problèmes montées, sans qu'elles se connaissent.
//
// ─── Pourquoi ───
//
// Deux déclencheurs, un seul mécanisme :
//
//  1. Sur bureau, une fiche s'ouvre PAR-DESSUS la liste, qui reste montée
//     avec les données de son ouverture (cf. ModaleRoute). Une fiche qui a
//     écrit (qualification, clôture, modification…) émet le signal en se
//     refermant : la liste ne montre pas de données périmées.
//  2. Le bouton « Rafraîchir » des pages principales : voir ce que les AUTRES
//     ont changé pendant que la page était ouverte (pas de temps réel — Static
//     Web App, pas de backend).
//
// Chaque `useIssues` monté écoute et se recharge en silence. Un simple
// événement de fenêtre : ni la fiche ni le bouton n'ont à savoir quelles
// listes la page affiche (le tableau de bord en a une cachée dans
// useDashboardData, l'accueil une seule pour deux sections…).

import { useEffect } from 'react'

const EVENEMENT = 'sce:problemes-modifies'

/**
 * Le rechargement de chaque liste, déposé par son écouteur : c'est ce qui
 * permet au bouton de tourner jusqu'au bout et de dire si ça a échoué.
 * Chaque promesse vaut `true` si la liste a pu être relue.
 */
interface DetailSignal {
  attentes: Promise<boolean>[]
}

/** Signal sans attente — fermeture d'une fiche. */
export function signalerProblemesModifies(): void {
  window.dispatchEvent(new CustomEvent<DetailSignal>(EVENEMENT, { detail: { attentes: [] } }))
}

/**
 * Recharge toutes les listes montées et attend qu'elles aient fini.
 * @returns false si au moins une n'a pas pu être relue (elle garde alors
 *          ses données précédentes).
 */
export async function rafraichirProblemes(): Promise<boolean> {
  const detail: DetailSignal = { attentes: [] }
  window.dispatchEvent(new CustomEvent<DetailSignal>(EVENEMENT, { detail }))
  const resultats = await Promise.all(detail.attentes)
  return resultats.every(Boolean)
}

/** Appelle `recharger` à chaque signal, tant que le composant est monté. */
export function useSurProblemesModifies(recharger: () => Promise<boolean>): void {
  useEffect(() => {
    function surSignal(e: Event): void {
      const attente = recharger()
      const detail = (e as CustomEvent<DetailSignal | undefined>).detail
      detail?.attentes.push(attente)
    }
    window.addEventListener(EVENEMENT, surSignal)
    return () => window.removeEventListener(EVENEMENT, surSignal)
  }, [recharger])
}
